import React, {useEffect, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';
import {toast} from 'react-toastify';
import GardenScene3D from './GardenScene3D';
import MobileControls from './MobileControls';
import SettingsModal from './SettingsModal'; // Import the modal
import { FaCog } from 'react-icons/fa'; // Import settings icon
import {DEFAULT_LOOK_SENSITIVITY, ZOOM_SENSITIVITY} from "./constants.js";
import { useWebSocket } from '../contexts/WebSocketContext'; // Import the hook

const GameContainer = styled.div`
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
    cursor: ${props => props.$isLocked ? 'none' : 'default'};
`;

const LockPrompt = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: rgba(0, 0, 0, 0.7);
    color: white;
    padding: 20px;
    border-radius: 8px;
    text-align: center;
    display: ${props => props.$show ? 'block' : 'none'};
    z-index: 10;
    pointer-events: none; // Let clicks pass through
`;

const SettingsIcon = styled.button`
    position: absolute;
    top: 15px;
    left: 15px;
    background: rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.5);
    color: white;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 20; // Above 3D scene, below modal
    pointer-events: auto; // Make sure it's clickable

    &:hover {
        background: rgba(255, 255, 255, 0.4);
    }

    /* Adjust if needed when pointer is locked */
    ${GameContainer}:has(:focus-visible) & { /* Example if lock affects focus */
       /* Maybe change appearance slightly */
    }
`;

const HUD = styled.div`
    position: absolute;
    top: 15px;
    right: 15px;
    background: rgba(0, 0, 0, 0.5);
    color: #fff;
    padding: 8px 12px;
    border-radius: 8px;
    font-size: 14px;
    line-height: 1.4;
    z-index: 20;
    pointer-events: none;
`;

const GameScreen = ({ selectedVegetable, onCharacterGrow }) => {
    const { isConnected, worldState, sendData, ws, pUUID, resumedPlayerState, myScore, computeCurrentGlow } = useWebSocket();
    //console.log(`--- GameScreen Render --- isConnected: ${isConnected}`); // <-- Add this
    const gameStartTime = useRef(Date.now());
    const minutesElapsedRef = useRef(0);
    const containerRef = useRef(null);
    const controlsRef = useRef({
        move: null,
        rotate: null,
        onZoom: null,
        onGrowth: null,
        onCharacterGrow: onCharacterGrow,
        updatePlayerState: null,
        setPlayerState: null, // NEW: allow applying resume state
    });
    const startsWithVowel = useMemo(() => {
        return /^[aeiou]/i.test(selectedVegetable.name);
    }, [selectedVegetable?.name]);
    const [isMobile, setIsMobile] = useState(false);
    const keyStates = useRef({});
    const [isPointerLocked, setIsPointerLocked] = useState(false);
    const [showLockPrompt, setShowLockPrompt] = useState(true);
    const [showSettings, setShowSettings] = useState(false);
    const [invertY, setInvertY] = useState(() => {
        try {
            const savedInvertY = localStorage.getItem('invertY');
            return savedInvertY !== null ? JSON.parse(savedInvertY) : false;
        } catch { return false; }
    });
    const [lookSensitivity, setLookSensitivity] = useState(() => {
        try {
            const savedSensitivity = localStorage.getItem('lookSensitivity');
            return savedSensitivity !== null ? JSON.parse(savedSensitivity) : DEFAULT_LOOK_SENSITIVITY;
        } catch { return DEFAULT_LOOK_SENSITIVITY; }
    });
    // NEW: Player name state persisted in localStorage
    const [playerName, setPlayerName] = useState(() => {
        try {
            return localStorage.getItem('playerName') || '';
        } catch { return ''; }
    });

    const handlePlayerNameChange = (name) => {
        const trimmed = (name || '').slice(0, 40);
        setPlayerName(trimmed);
        try { localStorage.setItem('playerName', trimmed); } catch {}
        // Send an immediate update to propagate name change
        sendData({
            type: 'playerUpdate',
            payload: { state: {
                position: playerStateRef.current.position,
                rotation: playerStateRef.current.characterRotation,
                velocity: [0,0,0],
                scale: playerStateRef.current.scale,
                isWalking: false,
                vegetableType: playerStateRef.current.vegetableType,
                name: trimmed,
                timestamp: Date.now(),
            } }
        });
    };

    // Apply resumed state when received and controls are ready (keep selectedVegetable type)
    useEffect(() => {
        if (!resumedPlayerState) return;
        const tryApply = () => {
            if (controlsRef.current && typeof controlsRef.current.setPlayerState === 'function') {
                controlsRef.current.setPlayerState(resumedPlayerState);
                // Sync local ref and use the user's selected vegetable type
                playerStateRef.current = {
                    ...playerStateRef.current,
                    position: resumedPlayerState.position || playerStateRef.current.position,
                    characterRotation: resumedPlayerState.rotation || playerStateRef.current.characterRotation,
                    scale: typeof resumedPlayerState.scale === 'number' ? resumedPlayerState.scale : playerStateRef.current.scale,
                    isWalking: false,
                    vegetableType: selectedVegetable?.name || 'default',
                };
                // Immediately announce current state so others see the newly selected type and name
                sendData({
                    type: 'playerUpdate',
                    payload: {
                        state: {
                            position: playerStateRef.current.position,
                            rotation: playerStateRef.current.characterRotation,
                            velocity: [0, 0, 0],
                            scale: playerStateRef.current.scale,
                            isWalking: false,
                            vegetableType: playerStateRef.current.vegetableType,
                            name: playerName || '',
                            timestamp: Date.now(),
                        }
                    }
                });
                return true;
            }
            return false;
        };
        if (!tryApply()) {
            const id = setInterval(() => {
                if (tryApply()) clearInterval(id);
            }, 50);
            return () => clearInterval(id);
        }
    }, [resumedPlayerState, selectedVegetable, sendData, playerName]);

    // --- Player Update Interval ---
    useEffect(() => {
        // GUARD: Only set up interval if connected AND we have an ID
        if (!isConnected || !selectedVegetable?.id) {
            console.log(`GameScreen Effect: Skipping interval setup (isConnected: ${isConnected}, ID: ${selectedVegetable?.id})`);
            return; // Interval should be cleared or wasn't started
        }

        // Last sent state for comparison
        const lastSentState = {
            position: [...playerStateRef.current.position],
            rotation: [...playerStateRef.current.characterRotation],
            scale: playerStateRef.current.scale,
            isWalking: !!playerStateRef.current.isWalking,
            vegetableType: playerStateRef.current.vegetableType || (selectedVegetable?.name || 'default'),
            name: playerName || '',
        };

        // Last time an update was sent (for heartbeat)
        let lastUpdateTime = Date.now();

        // Check if state has changed enough to warrant an update
        const hasSignificantChange = () => {
            const current = playerStateRef.current;

            // Immediate send when walking state toggles (start/stop)
            if (!!current.isWalking !== lastSentState.isWalking) {
                return true;
            }

            // Immediate send when vegetable type changes
            if ((current.vegetableType || '') !== (lastSentState.vegetableType || '')) {
                return true;
            }

            // Position change threshold (0.1 units)
            const posThreshold = 0.1;
            if (Math.abs(current.position[0] - lastSentState.position[0]) > posThreshold ||
                Math.abs(current.position[1] - lastSentState.position[1]) > posThreshold ||
                Math.abs(current.position[2] - lastSentState.position[2]) > posThreshold) {
                return true;
            }

            // Rotation change threshold (0.1 radians ≈ 5.7 degrees)
            const rotThreshold = 0.1;
            if (Math.abs(current.characterRotation[1] - lastSentState.rotation[1]) > rotThreshold) {
                return true;
            }

            // Scale change threshold (0.01)
            if (Math.abs(current.scale - lastSentState.scale) > 0.01) {
                return true;
            }

            // Name change
            if ((playerName || '') !== (lastSentState.name || '')) {
                return true;
            }

            return false;
        };

        const sendPositionUpdate = () => {
            if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
                console.log('sendPositionUpdate: WebSocket is not OPEN. Current state:', ws.current);
                 return; // Don't attempt to send if the socket isn't open
            }

            const now = Date.now();
            const heartbeatInterval = 3000; // 3 seconds

            // Send update if position changed OR it's time for a heartbeat
            if (hasSignificantChange() || (now - lastUpdateTime > heartbeatInterval)) {
                const current = playerStateRef.current;

                // Update last sent state
                lastSentState.position = [...current.position];
                lastSentState.rotation = [...current.characterRotation];
                lastSentState.scale = current.scale;
                lastSentState.isWalking = !!current.isWalking;
                lastSentState.vegetableType = current.vegetableType || lastSentState.vegetableType;
                lastSentState.name = playerName || '';
                lastUpdateTime = now;

                // Send the update
                sendData({
                    type: 'playerUpdate',
                    payload: {
                        state: {
                            position: current.position,
                            rotation: current.characterRotation,
                            velocity: current.velocity,
                            scale: current.scale,
                            isWalking: current.isWalking,
                            vegetableType: current.vegetableType || selectedVegetable?.name || 'default',
                            name: playerName || '',
                            timestamp: Date.now()
                        }
                    }
                });
            }
        };

        console.log("GameScreen Effect: Setting up playerUpdate interval (isConnected: true)");
        const intervalId = setInterval(sendPositionUpdate, 200); // 200ms = 5 updates per second

        // Cleanup function: Runs when component unmounts OR dependencies change
        return () => {
            console.log("GameScreen Effect: Cleaning up playerUpdate interval.");
            clearInterval(intervalId);
        };

    // *** CRUCIAL: Ensure dependencies are correct ***
    // ws ref itself doesn't need to be here, but isConnected and sendData do.
    }, [isConnected, sendData, selectedVegetable, ws, playerName]);

    // Filter out the current player from the list of players received
    const otherPlayers = useMemo(() => {
        if (!worldState?.players || !pUUID) return {};

        // Filter out current player based on pUUID, not vegetable ID
        const others = {};

        Object.entries(worldState.players).forEach(([id, playerData]) => {
            // Only include players that aren't the current player
            if (id !== pUUID) {
                others[id] = playerData;
            }
        });

        return others;
    }, [worldState?.players, pUUID]);


    // Detect mobile devices
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768 ||
                ('ontouchstart' in window) ||
                (navigator.maxTouchPoints > 0));
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Handle pointer lock
    useEffect(() => {
        const pointerLockSupported = 'pointerLockElement' in document || 'mozPointerLockElement' in document || 'webkitPointerLockElement' in document;
        if (!pointerLockSupported || isMobile) {
            setShowLockPrompt(false);
            return;
        }
        const handleLockChange = () => {
            const isLocked = !!(document.pointerLockElement || document.mozPointerLockElement || document.webkitPointerLockElement);
            setIsPointerLocked(isLocked);
            setShowLockPrompt(!isLocked);
            console.log("Pointer lock state changed:", isLocked ? "locked" : "unlocked");
        };
        const requestLock = (e) => {
            const settingsButton = e.target.closest('button[aria-label="Open settings"]');
            const modalContent = e.target.closest('.modal-content-class'); // FIX: match actual modal class

            if (showSettings || settingsButton || modalContent) {
                console.log("Pointer lock request prevented by settings UI.");
                return;
            }
            const container = containerRef.current;
            if (container && !document.pointerLockElement) {
                container.requestPointerLock = container.requestPointerLock || container.mozRequestPointerLock || container.webkitRequestPointerLock;
                try {
                    container.requestPointerLock();
                    console.log("Pointer lock requested");
                } catch (err) { console.error("Error requesting pointer lock:", err); }
            }
        };
        const handleLockError = (e) => { console.error("Pointer lock error:", e); };

        document.addEventListener('pointerlockchange', handleLockChange);
        document.addEventListener('mozpointerlockchange', handleLockChange);
        document.addEventListener('webkitpointerlockchange', handleLockChange);
        document.addEventListener('pointerlockerror', handleLockError);
        document.addEventListener('mozpointerlockerror', handleLockError);
        document.addEventListener('webkitpointerlockerror', handleLockError);
        const currentContainer = containerRef.current;
        if (currentContainer) { currentContainer.addEventListener('click', requestLock); }

        return () => {
            document.removeEventListener('pointerlockchange', handleLockChange);
            document.removeEventListener('mozpointerlockchange', handleLockChange);
            document.removeEventListener('webkitpointerlockchange', handleLockChange);
            document.removeEventListener('pointerlockerror', handleLockError);
            document.removeEventListener('mozpointerlockerror', handleLockError);
            document.removeEventListener('webkitpointerlockerror', handleLockError);
            if (currentContainer) { currentContainer.removeEventListener('click', requestLock); }
            if (document.exitPointerLock) { document.exitPointerLock(); }
        };
    }, [isMobile, showSettings]);


    // --- Input Handling Effect ---
    useEffect(() => {
        // Track key states
        const handleKeyDown = (e) => {
            const key = e.key.toLowerCase();
            const isTyping = ['input', 'textarea'].includes((e.target.tagName || '').toLowerCase()) || !!e.target.isContentEditable;
            const inModal = !!(e.target.closest('.modal-content-class'));

            // If typing in an input or inside the settings modal, do not block typing.
            if (isTyping || inModal) {
                if (key === 'escape') {
                    // Allow ESC to close settings even when focused inside modal
                    if (showSettings) setShowSettings(false);
                }
                return;
            }

            console.log('Key down:', key);

            // Prevent default for game keys only when not typing/in modal
            if (['w', 'a', 's', 'd', ' '].includes(key)) {
                e.preventDefault();
            }

            switch(key) {
                case 'w':
                case 'a':
                case 's':
                case 'd':
                    keyStates.current[key] = true;
                    updateMovement();
                    break;
                case ' ':
                    keyStates.current['space'] = true;
                    updateMovement();
                    break;
                case 'escape':
                    // Exit pointer lock OR close settings if open
                    if (showSettings) {
                        setShowSettings(false);
                    } else if (document.exitPointerLock) {
                        document.exitPointerLock();
                    }
                    break;
                default:
                    break;
            }
        };

        const handleKeyUp = (e) => {
            const key = e.key.toLowerCase();
            const isTyping = ['input', 'textarea'].includes((e.target.tagName || '').toLowerCase()) || !!e.target.isContentEditable;
            const inModal = !!(e.target.closest('.modal-content-class'));
            if (isTyping || inModal) return; // don't intercept typing in modal/inputs

            // Prevent default for game keys only when not typing/in modal
            if (['w', 'a', 's', 'd', ' '].includes(key)) {
                e.preventDefault();
            }

            switch(key) {
                case 'w':
                case 'a':
                case 's':
                case 'd':
                    keyStates.current[key] = false;
                    updateMovement();
                    break;
                case ' ':
                    keyStates.current['space'] = false;
                    updateMovement();
                    break;
                default:
                    break;
            }
        };

        // --- Mouse Wheel for Zoom (Desktop) ---
        const handleWheel = (e) => {
            const isTyping = ['input', 'textarea'].includes((e.target.tagName || '').toLowerCase()) || !!e.target.isContentEditable;
            const inModal = !!(e.target.closest('.modal-content-class'));
            if (isTyping || inModal) return; // don't zoom when interacting in modal/inputs
            // Only zoom if pointer is locked or maybe always on desktop?
            if (controlsRef.current && controlsRef.current.onZoom && !isMobile) {
                const delta = Math.sign(e.deltaY) * ZOOM_SENSITIVITY * 50;
                controlsRef.current.onZoom(delta);
            }
        };

        // Mouse controls for camera - only works when pointer is locked
        const handleMouseMove = (e) => {
            if (showSettings || !isPointerLocked) return;
            if (controlsRef.current?.rotate) {
                const movementX = e.movementX || 0;
                const movementY = e.movementY || 0;

                // Apply rotation using sensitivity constant
                controlsRef.current.rotate(
                    movementX * lookSensitivity, // Use constant
                    movementY * lookSensitivity  // Use constant
                );
            }
        };

        // Apply movement based on current key states
        const updateMovement = () => {
            // Check if settings modal is open - if so, don't process game movement
            if (showSettings) {
                // Ensure movement stops if settings are opened while moving
                if (controlsRef.current && typeof controlsRef.current.move === 'function') {
                    controlsRef.current.move(0, 0, 0);
                }
                return;
            }

            console.log('Updating movement:', keyStates.current);
            if (controlsRef.current && typeof controlsRef.current.move === 'function') {
                const moveX = (keyStates.current['a'] ? -1 : 0) + (keyStates.current['d'] ? 1 : 0);
                const moveZ = (keyStates.current['w'] ? -1 : 0) + (keyStates.current['s'] ? 1 : 0);
                const jump = keyStates.current['space'] ? 1 : 0;

                console.log("Calling move with:", moveX, moveZ, jump);
                // Send movement input to 3D scene
                controlsRef.current.move(moveX, moveZ, jump);
            } else {
                console.error("Move function not found in controlsRef:", controlsRef.current);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('wheel', handleWheel);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('wheel', handleWheel);
        };
    }, [isPointerLocked, isMobile, showSettings, lookSensitivity, invertY]); // Add invertY dependency


    // Mobile control handlers
    const handleMobileMove = (moveX, moveZ, jump) => {
        // Don't process if settings are open
        if (showSettings) return;
        if (controlsRef.current && controlsRef.current.move) {
            controlsRef.current.move(moveX, moveZ, jump);
        }
    };

    const handleMobileRotate = (deltaX, deltaY) => {
        // deltaX, deltaY are raw pixel differences from MobileControls
        if (showSettings) return;
        if (controlsRef.current && controlsRef.current.rotate) {
            // *** REMOVE INVERSION LOGIC FROM HERE ***
            // const inversionFactor = invertY ? -1 : 1;
            // const adjustedDeltaY = deltaY * inversionFactor;

            // Apply sensitivity state directly to the raw deltas
            controlsRef.current.rotate(
                deltaX * lookSensitivity,
                deltaY * lookSensitivity // Pass the sensitivity-adjusted *original* deltaY
            );
        }
    };

    const handleMobileJump = () => {
        // Don't process if settings are open
        if (showSettings) return;
        if (controlsRef.current && controlsRef.current.move) {
            controlsRef.current.move(0, 0, 1); // Trigger jump
        }
    };

    // Add a handler for zoom events coming from MobileControls
    const handleMobileZoom = (zoomDelta) => {
        if (controlsRef.current && controlsRef.current.onZoom) {
            // Pass the delta directly to the VegetablePlayer's zoom handler
            controlsRef.current.onZoom(zoomDelta);
        }
    };

    // Add a handler function for growth notifications
    const handleCharacterGrow = (newScale, maxScale) => {
        // Show appropriate toast based on growth progress
        if (newScale < maxScale) {
            toast.dark('You have grown.', {
                position: "top-center",
                autoClose: 5000,
                hideProgressBar: true,
            });
        } else {
            toast.dark('You have reached maximum growth.', {
                position: "top-center",
                autoClose: 5000,
                hideProgressBar: true,
            });
        }
    };

    useEffect(() => {
        // Update minutes elapsed every minute, but don't trigger re-renders
        const minuteTimer = setInterval(() => {
            minutesElapsedRef.current = Math.floor(
                (Date.now() - gameStartTime.current) / (1000 * 60)
            );
        }, 60000); // Check every minute

        // Random "Vegetables don't have eyes" message
        const randomEyesMessageTimer = setInterval(() => {
            // Only check after 3 minutes have passed
            if (minutesElapsedRef.current >= 3) {
                // 1 in 30 chance (approximately 3.33%)
                if (Math.random() < 1/30) {
                    toast.dark('Vegetables don\'t have eyes.', {
                        position: "bottom-center",
                        autoClose: 3000,
                        hideProgressBar: true,
                    });
                }
            }
        }, 60000); // Check every minute

        // // Fixed interval "You have grown" message - every 9 minutes
        // const growthMessageTimer = setInterval(() => {
        //     toast.dark('You have grown.', {
        //         position: "top-center",
        //         autoClose: 5000,
        //         hideProgressBar: true,
        //     });
        // }, 9 * 60000); // Every 9 minutes

        // Fixed interval "You are still a [vegetable]" message - every 13 minutes
        const stillVegetableTimer = setInterval(() => {
            toast.dark(`You are still a${startsWithVowel ? 'n' : ''} ${selectedVegetable.name.toLowerCase()}.`, {
                position: "top-center",
                autoClose: 8000,
                hideProgressBar: true,
            });
        }, 13 * 60000); // Every 13 minutes

        // Cleanup all timers on unmount
        return () => {
            clearInterval(minuteTimer);
            clearInterval(randomEyesMessageTimer);
            // clearInterval(growthMessageTimer);
            clearInterval(stillVegetableTimer);
        };
    }, [selectedVegetable, startsWithVowel]);

    // In the GameScreen component, still keep the timer to trigger growth:
    useEffect(() => {
        // Set up a timer to try growing the character every 9 minutes
        const growthTimer = setInterval(() => {
            if (controlsRef.current && controlsRef.current.onGrowth) {
                controlsRef.current.onGrowth();
                // We no longer need to check the return value here
                // as the notification is handled by the callback
            }
        }, 9 * 60000); // Every 9 minutes

        return () => clearInterval(growthTimer);
    }, []);

    // Ref to hold latest player state for sending updates
    // This needs to be updated from VegetablePlayer, perhaps via controlsRef callback
    const playerStateRef = useRef({
        position: [0, 1, 0],
        characterRotation: [0, 0, 0],
        scale: 0.25, // Initial scale
        isWalking: false,
        vegetableType: selectedVegetable?.name || 'default',
    });

    // Update playerStateRef via callback from 3D scene
    useEffect(() => {
        if (controlsRef.current) {
            controlsRef.current.updatePlayerState = (newState) => {
                playerStateRef.current = {
                    ...newState,
                    vegetableType: selectedVegetable?.name || 'default'
                };
            };
        }
        // Cleanup if necessary
        return () => {
            if (controlsRef.current) {
                controlsRef.current.updatePlayerState = null;
            }
        }
    }, [controlsRef, selectedVegetable]); // Run when controlsRef is potentially assigned

    // --- Settings Modal Handlers ---
    const openSettings = () => {
        // If pointer is locked, exit lock first
        if (isPointerLocked && document.exitPointerLock) {
            document.exitPointerLock();
        }
        setShowSettings(true);
    };

    const closeSettings = () => {
        setShowSettings(false);
        // Optional: Re-request pointer lock if desired after closing settings?
        // Maybe better to let user click back into the game.
    };

    const handleInvertYChange = (newInvertValue) => {
        setInvertY(!!newInvertValue);
        try { // Add try/catch for localStorage
            localStorage.setItem('invertY', JSON.stringify(newInvertValue));
        } catch (error) {
            console.error("Failed to save invertY setting:", error);
        }
    };

    // Add handler for sensitivity change
    const handleSensitivityChange = (newSensitivityValue) => {
        console.log("GameScreen: Sensitivity changed TO:", newSensitivityValue);
        setLookSensitivity(newSensitivityValue);
        try { // Add try/catch for localStorage
            localStorage.setItem('lookSensitivity', JSON.stringify(newSensitivityValue));
        } catch (error) {
            console.error("Failed to save lookSensitivity setting:", error);
        }
    };



    return (
        <GameContainer ref={containerRef} $isLocked={isPointerLocked}>
            {/* Settings Icon - Render always, but handle interaction */}
            <SettingsIcon onClick={openSettings} aria-label="Open settings">
                <FaCog size={20} />
            </SettingsIcon>

            {/* Pass world state down */}
            <GardenScene3D
                selectedVegetable={selectedVegetable}
                controlsRef={controlsRef}
                onGrow={handleCharacterGrow}
                invertY={invertY} // Pass setting for potential direct use if needed
                // Pass world state elements
                otherPlayers={otherPlayers}
                plants={worldState?.plants || []}
                walls={worldState?.walls || []}
                tree={worldState?.tree || null}
                dayLengthMinutes={worldState?.dayLengthMinutes}
                initialTimeOfDay={worldState?.initialTimeOfDay}
                cycleEpochMs={worldState?.cycleEpochMs || 0}
            />

            {/* Score HUD */}
            <HUD>
                <div>Diamonds: {myScore || 0}</div>
                <div>Glow: {computeCurrentGlow().toFixed(1)}</div>
            </HUD>

            {isMobile && (
                <MobileControls
                    onMove={handleMobileMove}
                    onRotate={handleMobileRotate}
                    onJump={handleMobileJump}
                    onZoom={handleMobileZoom}
                />
            )}

            <LockPrompt $show={showLockPrompt && !isPointerLocked && !showSettings && !isMobile}>
                <h3>Click to play</h3>
                <p>Click anywhere to enable mouse control. Press ESC to exit.</p>
            </LockPrompt>

            {/* Settings Modal - Add class for pointer lock check */}
            <SettingsModal
                className="settings-modal-content" // Add a class
                isOpen={showSettings}
                onClose={closeSettings}
                invertY={invertY}
                onInvertYChange={handleInvertYChange}
                lookSensitivity={lookSensitivity}
                onSensitivityChange={handleSensitivityChange}
                playerName={playerName}
                onPlayerNameChange={handlePlayerNameChange}
            />
        </GameContainer>
    );
}

export default GameScreen;
